import axios from "axios";
import {createClient} from "@connectrpc/connect";
import {apiTransport} from "../api/connectTransport";
import {PageThemeService} from "../gen/kitecorp/transferify/storefront/v1/page_theme_pb";
import {pageThemeOf, pageThemeRequestOf} from "./brandMessages";
import type {AssertNoSpecDrift, Schemas, SpecDrift} from "api/schemaAssert";
import type {SponsorOptions, TakeoverTemplateKey} from "sponsor/sponsorBooking";

/**
 * The sender's own DOWNLOAD-PAGE THEME (api#511) — the animated backdrop a PREMIUM sender puts behind
 * the recipient card, using the same treatments a sponsor can buy (five since ui#574/ui#576).
 *
 * Sibling of {@link AccountBrandApi} rather than part of it: the brand is a logo and an accent snapshot
 * taken at transfer creation, this is a live, account-level decoration resolved when the recipient page
 * is served. Entitlement is the api's: every write here answers 400 below Premium, so the client gate is
 * UX only. The READ is ungated on purpose — a sender who lapses must still be able to see, and remove,
 * the theme they configured while they were paying.
 *
 * The api ALSO enforces each treatment's arity (`BackdropTemplate.accepts`), which is why a write here is
 * addressed with `themeImagesPublished`'s slice rather than with everything the sender has chosen: three
 * images under Hero is a guaranteed 400, not a preference.
 */

/**
 * One image in the sender's LIBRARY, and whether the SAVED theme renders it (api#471).
 *
 * `attached` is the server's answer about the live theme, never the editor's draft — the sheet's own
 * selection lives in `brand/themeLibrary.ts`.
 */
export type PageThemeLibraryImage = {
	assetId: string;
	url: string;
	attached: boolean;
};

/**
 * The `PageThemeService` theme. `template` is null while no theme has ever been saved.
 *
 * `images` is the sender's WHOLE library, not the slice the theme renders (api#471). It used to be two
 * parallel `assetIds`/`imageUrls` arrays holding the attached images only, which meant a reloaded sheet
 * could not show — or even name — anything the sender had uploaded and not published, though the rows and
 * the stored bytes were still there. One list of records also retires the defensive zip that paired the two
 * arrays: they cannot disagree if they are never apart.
 */
export type PageTheme = {
	enabled: boolean;
	template: TakeoverTemplateKey | null;
	/**
	 * The sender's per-treatment settings, or `null` when the saved treatment takes none
	 * (kitecorp/transferify-api-service#1140). A BACKDROP always reads `null`: the api refuses settings
	 * on one, so this is the column's own answer rather than a client default.
	 */
	options: SponsorOptions | null;
	images: PageThemeLibraryImage[];
};

/** `SetPageTheme` body. `assetIds` are the ids returned by the asset upload, in order. */
export type PageThemeUpdate = {
	template: TakeoverTemplateKey;
	enabled: boolean;
	/** `null` for a backdrop, which is what the api stores for a treatment that takes no settings. */
	options: SponsorOptions | null;
	assetIds: string[];
};

/** `POST /account/brand/theme/assets` response. */
export type PageThemeAsset = {
	assetId: string;
	url: string;
};

/* OpenAPI conformance (#208): `tsc -b` fails when the asset upload's answer drifts from the generated spec type. */
export type _PageThemeAsset = AssertNoSpecDrift<SpecDrift<PageThemeAsset, Schemas["ThemeAsset"]>>;

const themeClient = () => createClient(PageThemeService, apiTransport);

export class AccountThemeApi {
	static async getTheme(): Promise<PageTheme> {
		return pageThemeOf((await themeClient().getPageTheme({})).theme);
	}

	static async saveTheme(update: PageThemeUpdate): Promise<PageTheme> {
		return pageThemeOf((await themeClient().setPageTheme(pageThemeRequestOf(update))).theme);
	}

	/**
	 * Uploads one image. Stays on REST: it is a multipart body and the contract has no message for one. The multipart
	 * uploads are batch 7 of kitecorp/transferify#418, which kitecorp/transferify-api-service#3161 tracks.
	 *
	 * The multipart part is named `image`, the same name the sponsor asset endpoint uses, and the name the api binds;
	 * a mismatch here surfaces as an unhelpful 400 rather than a validation message.
	 */
	static async uploadImage(image: File): Promise<PageThemeAsset> {
		const body = new FormData();
		body.append("image", image);
		const {data} = await axios.post<PageThemeAsset>("/account/brand/theme/assets", body, {
			headers: {"Content-Type": "multipart/form-data"},
		});
		return data;
	}

	/**
	 * Clears the theme entirely. Ungated, so a lapsed sender can always turn their own decoration off.
	 *
	 * The sender's IMAGES survive this — the response carries the whole library back, every entry now
	 * unattached — so clearing is a way to take the decoration down, not a way to lose the uploads.
	 */
	static async removeTheme(): Promise<PageTheme> {
		return pageThemeOf((await themeClient().resetPageTheme({})).theme);
	}

	/**
	 * Deletes one image from the library for good: the api drops the row AND the stored bytes, which is what
	 * stops a library the sender no longer wants from being billed forever (api#471).
	 *
	 * Answers 400 while the image is part of a theme the sender's pages are showing right now. The editor
	 * mirrors that refusal in `brand/themeLibrary.ts#canDeleteImage` so the control is disabled rather than
	 * offered and then refused, but the api remains the gate.
	 */
	static async deleteImage(assetId: string): Promise<PageTheme> {
		return pageThemeOf((await themeClient().deleteThemeImage({assetId})).theme);
	}
}
